import { useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import axios from "../util/axios";
import config from "../config/config";

interface UserOption {
  value: string | number;
  label: string;
}

interface UserSearchSelectProps {
  value?: UserOption | null;
  onChange: (selectedUser: UserOption | null) => void;
  placeholder?: string;
  className?: string;
  classNamePrefix?: string;
  isClearable?: boolean;
}

const UserSearchSelect = ({
  value,
  onChange,
  placeholder = "Search User",
  className = "react-select-container",
  classNamePrefix = "react-select",
  isClearable = true,
}: UserSearchSelectProps) => {
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadUserOptions = async (inputValue: string) => {
    return new Promise<UserOption[]>((resolve) => {
      // Clear any existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set a new timeout for 300ms
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          const controller = new AbortController();
          const signal = controller.signal;

          // Updated URL format to use direct query parameters
          const response = await axios.get(
            `${config.defaults.api_url}/user/all?page=1&take=5&search=${encodeURIComponent(inputValue)}`,
            {
              signal: signal,
            },
          );

          resolve(
            response.data.results.data.map((user: any) => ({
              value: user.id,
              label: user.email,
            })),
          );
        } catch (error) {
          if (axios.isCancel(error)) {
            // Request was cancelled, no need to show error
            resolve([]);
          }

          console.error("Error loading user options:", error);
          toast.error("Failed to load users");
          resolve([]);
        }
      }, 300);
    });
  };

  // Clean up timeout on component unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AsyncSelect
      isClearable={isClearable}
      placeholder={placeholder}
      noOptionsMessage={() => "No results"}
      loadOptions={loadUserOptions}
      value={value}
      onChange={(v: any) => {
        onChange(v);
      }}
      className={className}
      classNamePrefix={classNamePrefix}
    />
  );
};

export default UserSearchSelect;
